import React, { useEffect, useRef } from 'react'

import {
    SpinButtonSectionStore,
    useBetSliderStore,
    useConfigurationStore,
    useLuckySpinsStore,
    useMenuStore,
    useNavigationStore,
    useSpinButtonSectionStore,
} from '@stores'

import NavButton from '@components/Navigation/NavButton'

import fullscreenDisabled from '@assets/button-fullscreen-enabled.svg'
import fullscreenEnabled from '@assets/button-fullscreenexit-enabled.svg'
import musicIconDisabled from '@assets/button-musicoff-enabled.svg'
import volumeIconDisabled from '@assets/button-volumeoff-enabled.svg'
import menuIconClosed from '@assets/close-button.svg'
import homeIcon from '@assets/home-button.svg'
import menuIcon from '@assets/menu-button.svg'
import musicIcon from '@assets/music-button.svg'
import volumeIcon from '@assets/volume-button.svg'
import {
    BackgroundMusicClickedMessage,
    BaseGameType,
    ChangeOpacityComponents,
    ChangeOpacityMessagePrefix,
    EndTurboMessage,
    FullscreenChangedMessage,
    FullscreenClickedMessage,
    GameUIMessageType,
    HasBackgroundMusicMessage,
    HomeButtonClickedMessage,
    MenuClickedMessage,
    // MenuClickedMessage,
    MusicButtonToggledMessage,
    SendHideStateMessage,
    SendStopStateMessage,
    SendStopStateWithWinMessage,
    SetIsHomeButtonDisabledMessage,
    SetIsMenuButtonDisabledMessage,
    SetIsMenuOpenMessage,
    SoundToggledMessage,
    SpinButtonSendBeforeGambleStateMessage,
    SpinButtonSendIdleMessage,
    SpinButtonSendIdleWithoutGambleStateMessage,
    StartTurboMessage,
    ViewportType,
} from 'game-ui-types'
import { BonusType } from '@amusnet/bonuses-http-client'
import { BET_STEPPER_CONFIGS } from '@configs/gameConfigurations'
import { Menu } from '../'
import { useSoundStore } from '@stores/soundStore'
import { SOUNDS } from '@constants/sounds'
import { cn, hasFlag, useOpacityToggle } from '@amusnet/react-ui'
import { gameAnalytics } from '@utils/gameAnalytics'

const Navigation: React.FC = () => {
    const viewport = useConfigurationStore((state) => state.viewport)
    const isReplayMode = useConfigurationStore((state) => state.isReplayMode)
    const isSoundMuted = useConfigurationStore((state) => state.isSoundMuted)
    const isBackgroundMusicMuted = useConfigurationStore((state) => state.isBackgroundMusicMuted)
    const isFullscreen = useConfigurationStore((state) => state.isFullscreen)
    const setIsSoundMuted = useConfigurationStore((state) => state.setIsSoundMuted)
    const setIsBackgroundMusicMuted = useConfigurationStore((state) => state.setIsBackgroundMusicMuted)
    const setIsFullscreen = useConfigurationStore((state) => state.setIsFullscreen)
    const gameType = useConfigurationStore((state) => state.gameType)
    const shouldUseExternalMenu = useConfigurationStore((state) => state.shouldUseExternalMenu)
    const isExpoTournamentModeHidden = useConfigurationStore((state) => state.isExpoTournamentModeHidden)
    const shouldHideFullScreenButton = useConfigurationStore((state) => state.shouldHideFullScreenButton)

    const isMenuOpen = useNavigationStore((state) => state.isMenuOpen)
    const setIsMenuOpen = useNavigationStore((state) => state.setIsMenuOpen)
    const setShouldOpenMenuDefaultSection = useMenuStore((state) => state.setShouldOpenDefaultSection)
    const setTargetMenuSectionId = useMenuStore((state) => state.setTargetMenuSectionId)

    const isScrolling = useBetSliderStore((state) => state.isScrolling)
    const betGroup = useBetSliderStore((state) => state.betGroup)

    const isMenuButtonDisabled = useNavigationStore((state) => state.isMenuDisabled)
    const setIsMenuButtonDisabled = useNavigationStore((state) => state.setIsMenuDisabled)
    const isHomeButtonDisabled = useNavigationStore((state) => state.isHomeDisabled)
    const setIsHomeButtonDisabled = useNavigationStore((state) => state.setIsHomeDisabled)
    const hasBackgroundMusic = useNavigationStore((state) => state.hasBackgroundMusic)
    const setHasBackgroundMusic = useNavigationStore((state) => state.setHasBackgroundMusic)
    const isMusicButtonDisabled = useNavigationStore((state) => state.isMusicButtonDisabled)
    const setIsMusicButtonDisabled = useNavigationStore((state) => state.setIsMusicButtonDisabled)

    const playEngineSound = useSoundStore((state) => state.playEngineSound)

    // navigation left wrapper config
    const navigationLeftWrapperConfigStyles = useConfigurationStore(
        (state) => state.getComponentConfig('NavigationLeftWrapper').styles
    )

    // navigation right wrapper config
    const navigationRightWrapperConfigStyles = useConfigurationStore(
        (state) => state.getComponentConfig('NavigationRightWrapper').styles
    )

    // navigation config
    const navigationConfigStyles = useConfigurationStore((state) => state.getComponentConfig('Navigation').styles)
    const navigationConfigPosition = useConfigurationStore((state) => state.getComponentConfig('Navigation').position)
    const navigationComponent = useRef<HTMLDivElement | null>(null)

    useOpacityToggle(ChangeOpacityComponents.NAVIGATION, navigationComponent)

    const isLongPress = useSpinButtonSectionStore((state) => state.isLongPress)
    const spinButtonState = useSpinButtonSectionStore((state: SpinButtonSectionStore) => state.state)

    const isBetInProgress = useSpinButtonSectionStore((state) => state.isBetInProgress)

    const isLongPressRef = useRef(isLongPress)
    const spinButtonStateRef = useRef(spinButtonState)
    const isBetInProgressRef = useRef(isBetInProgress)
    const isScrollingRef = useRef(isScrolling)

    isScrollingRef.current = isScrolling

    const shouldHideHomeButton = hasFlag('shouldHideHomeButton')

    if (isExpoTournamentModeHidden) return null

    const updateHomeButtonState = (isDisabled: boolean) => {
        const isInLS = useLuckySpinsStore.getState().activeBonus?.type === BonusType.LUCKY_SPIN

        setIsHomeButtonDisabled(isInLS ? true : isDisabled)
    }

    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            const rawData = event.data
            const { type, payload } = rawData

            if (
                type === `${ChangeOpacityMessagePrefix}${ChangeOpacityComponents.HOME_BUTTON}` &&
                window.parent !== window
            ) {
                window.parent.postMessage({ type: GameUIMessageType.CHANGE_OPACITY_HOME_BUTTON, payload }, '*')
            }

            switch (type) {
                case GameUIMessageType.HAS_BACKGROUND_MUSIC: {
                    const data = rawData as HasBackgroundMusicMessage

                    setHasBackgroundMusic(data.hasBackgroundMusic)
                    break
                }
                case GameUIMessageType.SPIN_BUTTON_SEND_HIDE_STATE:
                case GameUIMessageType.SPIN_BUTTON_SEND_STOP_STATE:
                case GameUIMessageType.SPIN_BUTTON_SEND_STOP_STATE_WITH_WIN:
                case GameUIMessageType.START_TURBO: {
                    const data = rawData as
                        SendHideStateMessage | SendStopStateMessage | SendStopStateWithWinMessage | StartTurboMessage

                    setIsMenuButtonDisabled(data.isMenuButtonDisabled ?? true)
                    updateHomeButtonState(data.isHomeButtonDisabled ?? true)
                    break
                }

                case GameUIMessageType.SPIN_BUTTON_SEND_GAMBLE_STATE:
                case GameUIMessageType.SPIN_BUTTON_SEND_WAITING_FOR_FREESPIN_ANIMATION_START_STATE: {
                    setIsMenuButtonDisabled(true)
                    setIsHomeButtonDisabled(true)
                    setIsMusicButtonDisabled(true)
                    break
                }

                case GameUIMessageType.SPIN_BUTTON_SEND_IDLE_STATE:
                case GameUIMessageType.SPIN_BUTTON_SEND_IDLE_WITHOUT_GAMBLE_STATE:
                case GameUIMessageType.SPIN_BUTTON_SEND_BEFORE_GAMBLE_STATE:
                case GameUIMessageType.END_TURBO: {
                    const data = rawData as
                        | SpinButtonSendIdleMessage
                        | SpinButtonSendIdleWithoutGambleStateMessage
                        | SpinButtonSendBeforeGambleStateMessage
                        | EndTurboMessage

                    setIsMenuButtonDisabled(data.isMenuButtonDisabled ?? false)
                    updateHomeButtonState(data.isHomeButtonDisabled ?? false)
                    setIsMusicButtonDisabled(isMusicButtonDisabled ?? true)
                    break
                }
                case GameUIMessageType.FULLSCREEN_CHANGED: {
                    const data = rawData as FullscreenChangedMessage

                    setIsFullscreen(data.isFullscreen)
                    break
                }
                case GameUIMessageType.SET_IS_MENU_OPEN: {
                    const data = rawData as SetIsMenuOpenMessage

                    setShouldOpenMenuDefaultSection(false)
                    setIsMenuOpen(data.isMenuOpen)
                    break
                }
                case GameUIMessageType.SET_MENU_SECTION_ID: {
                    const data = rawData

                    setTargetMenuSectionId(data.payload.id)

                    break
                }
                case GameUIMessageType.SET_IS_HOME_BUTTON_DISABLED: {
                    const data = rawData as SetIsHomeButtonDisabledMessage

                    updateHomeButtonState(data.isDisabled)
                    break
                }
                case GameUIMessageType.SET_IS_MENU_BUTTON_DISABLED: {
                    const data = rawData as SetIsMenuButtonDisabledMessage

                    setIsMenuButtonDisabled(data.isDisabled)
                    break
                }
                case GameUIMessageType.MUSIC_BUTTON_TOGGLED: {
                    const data = rawData as MusicButtonToggledMessage

                    setIsBackgroundMusicMuted(data.isMuted, false)
                    break
                }
                case GameUIMessageType.SOUND_TOGGLED: {
                    const data = rawData as SoundToggledMessage

                    setIsSoundMuted(data.isMuted, false)
                    break
                }
            }
        }

        window.uiBus.addEventListener('message', handleMessage, false)

        return () => {
            window.uiBus.removeEventListener('message', handleMessage, false)
        }
    }, [])

    useEffect(() => {
        isLongPressRef.current = isLongPress
    }, [isLongPress])

    useEffect(() => {
        isBetInProgressRef.current = isBetInProgress
    }, [isBetInProgress])

    useEffect(() => {
        spinButtonStateRef.current = spinButtonState
    }, [spinButtonState])

    useEffect(() => {
        if (window.parent !== window) {
            window.parent.postMessage(
                {
                    type: GameUIMessageType.CHANGE_OPACITY_HOME_BUTTON,
                    payload: {
                        isInteractive: !isHomeButtonDisabled,
                    },
                },
                '*'
            )
        }
    }, [isHomeButtonDisabled])

    // TODO: remove when promo removed from platform
    useEffect(() => {
        if (window.parent !== window) {
            window.parent.postMessage(
                {
                    type: GameUIMessageType.CHANGE_OPACITY_PROMO_WIDGET,
                    payload: {
                        opacity: isMenuOpen ? 0 : 1,
                        isInteractive: !isMenuOpen,
                    },
                },
                '*'
            )
        }
    }, [isMenuOpen])

    const handleFullscreenClick = () => {
        setIsFullscreen(!isFullscreen)
        gameAnalytics.fullScreenToggled(!isFullscreen)

        const fullscreenClickedMessage: FullscreenClickedMessage = { type: GameUIMessageType.FULLSCREEN_CLICKED }

        window.uiBus.postMessage(fullscreenClickedMessage)
    }
    const handleMusicClick = () => {
        if (isLongPressRef.current) return

        setIsBackgroundMusicMuted(!isBackgroundMusicMuted)

        // TODO: Delete this after new menu is tested
        if (!shouldUseExternalMenu) {
            const backgroundMusicMutedMessage: BackgroundMusicClickedMessage = {
                type: GameUIMessageType.BACKGROUND_MUSIC_CLICKED,
            }

            window.uiBus.postMessage(backgroundMusicMutedMessage)
        }
    }
    const handleSoundClick = () => {
        setIsSoundMuted(!isSoundMuted)
    }
    const handleMenuClick = () => {
        if (isLongPressRef.current) return

        setIsMenuOpen(!isMenuOpen)
        playEngineSound(SOUNDS.SHOW_DENOMINATIONS)

        if (!shouldUseExternalMenu) {
            const menuClickedMessage: MenuClickedMessage = { type: GameUIMessageType.MENU_CLICKED }

            window.uiBus.postMessage(menuClickedMessage)
        }
    }
    const handleHomeClick = () => {
        if (isLongPressRef.current) return

        gameAnalytics.homeButtonClicked()

        const homeButtonClickedMessage: HomeButtonClickedMessage = { type: GameUIMessageType.HOME_BUTTON_CLICKED }

        window.uiBus.postMessage(homeButtonClickedMessage)

        const communicator = window.bottle?.container?.CommunicationManager

        if (!communicator) {
            // new slot-library has communicator instead of communicator manager
            // it handles the logic on its own - delete once all engines use the new version
            return
        }

        const original = communicator.notify

        communicator.notify = function (name: string, data: unknown) {
            original.call(this, name, data)

            if (name === 'HideGame') {
                const lobbyArgs = {
                    mgsAuthToken: (window.portalConfig as Record<string, string>)?.['mgs-auth-token'],
                    baseUrl: new URL('..', document.baseURI).href.replace(/\/$/, ''),
                }

                const { integration, launchUrl } = window

                if (launchUrl) {
                    Object.assign(lobbyArgs, { launchUrl })
                }

                if (integration?.launchLobby) {
                    integration?.launchLobby(lobbyArgs)
                } else if (typeof window.openLobby === 'function') {
                    window.openLobby(lobbyArgs)
                }
            }
        }
    }

    const shouldHideButton =
        gameType === BaseGameType.SLOT_CONFIG_E && viewport === ViewportType.MOBILE_PORTRAIT && !isMenuOpen

    const navigationRightWrapperMobileLandscapeStyles =
        !hasBackgroundMusic &&
        viewport === ViewportType.MOBILE_LANDSCAPE &&
        !betGroup &&
        !BET_STEPPER_CONFIGS.includes(gameType)
            ? 'flex-col'
            : ''

    return (
        <>
            <div
                ref={navigationComponent}
                className={cn('absolute left-0 w-full z-[10] pointer-events-none', navigationConfigStyles)}
                style={{
                    ...navigationConfigPosition,
                }}
            >
                <div className={cn(navigationLeftWrapperConfigStyles, 'pointer-events-none')}>
                    {shouldHideHomeButton || isReplayMode ? null : (
                        <NavButton
                            id="home-button"
                            componentName={ChangeOpacityComponents.HOME_BUTTON}
                            icon={homeIcon}
                            showBorder={false}
                            onClick={handleHomeClick}
                            disabled={isHomeButtonDisabled || isScrollingRef.current}
                        />
                    )}
                    {viewport === ViewportType.DESKTOP && !shouldHideFullScreenButton && !isReplayMode && (
                        <NavButton
                            id="fullscreen-button"
                            icon={isFullscreen ? fullscreenEnabled : fullscreenDisabled}
                            showBorder={false}
                            onClick={handleFullscreenClick}
                        />
                    )}
                </div>
                <div
                    className={cn(
                        navigationRightWrapperConfigStyles,
                        'pointer-events-none',
                        navigationRightWrapperMobileLandscapeStyles
                    )}
                >
                    {hasBackgroundMusic && (
                        <NavButton
                            id="music-button"
                            componentName={ChangeOpacityComponents.MUSIC_BUTTON}
                            icon={isBackgroundMusicMuted || isSoundMuted ? musicIconDisabled : musicIcon}
                            showBorder={false}
                            onClick={handleMusicClick}
                            disabled={isMusicButtonDisabled || isSoundMuted}
                            classNames={shouldHideButton ? 'invisible' : ''}
                        />
                    )}
                    <NavButton
                        id="sound-button"
                        componentName={ChangeOpacityComponents.SOUND_BUTTON}
                        icon={isSoundMuted ? volumeIconDisabled : volumeIcon}
                        showBorder={false}
                        onClick={handleSoundClick}
                        classNames={shouldHideButton ? 'invisible' : ''}
                    />
                    {!isReplayMode && (
                        <NavButton
                            id="menu-button"
                            componentName={ChangeOpacityComponents.MENU_BUTTON}
                            icon={!isMenuOpen ? menuIcon : menuIconClosed}
                            showBorder={false}
                            onClick={handleMenuClick}
                            disabled={isMenuButtonDisabled || isLongPress}
                        />
                    )}
                </div>
            </div>
            {isMenuOpen && shouldUseExternalMenu && <Menu />}
        </>
    )
}

export default Navigation
